import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { brl, data } from "@/lib/format";
import { paraCentavos } from "@/lib/dinheiro";
import {
  PageHeader,
  Panel,
  PanelHeader,
  Th,
  Td,
  EmptyState,
  Stat,
  Campo,
  Escolha,
  StatusBadge,
  Erro,
  GradeFios,
} from "@/components/parker";
import { useContas, useExtrato, useLancamentos, useIndice } from "@/lib/dados";
import {
  adicionarMovimento,
  conciliarMovimento,
  tratarMovimento,
  desfazerConciliacao,
} from "@/lib/fns";
import { useSessao } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { MovimentoExtrato } from "@/lib/tipos";
import { ImportarExtrato } from "@/components/importar-extrato";

export const Route = createFileRoute("/_authenticated/conciliacao")({ component: Conciliacao });

const hoje = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });

/** Lançamentos que já podem casar com um movimento do extrato. */
const CONCILIAVEIS = ["aprovado", "pago", "divergente"];

/** O que a caixa aberta embaixo de um movimento está esperando. */
type Pendencia =
  | { tipo: "divergencia"; movimentoId: string; lancamentoId: string; diferenca: number }
  | { tipo: "tratar"; movimentoId: string };

function Conciliacao() {
  const { podeEscrever } = useSessao();
  const contas = useContas();
  const extrato = useExtrato();
  const lancamentos = useLancamentos();
  const nomeConta = useIndice(contas.dados);

  const [novo, setNovo] = useState({ contaBancariaId: "", data: hoje(), descricao: "", valor: "" });
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [pendencia, setPendencia] = useState<Pendencia | null>(null);
  const [justificativa, setJustificativa] = useState("");

  if (extrato.erro) return <Erro mensagem={extrato.erro} />;

  const movimentos = extrato.dados;
  const divergentes = movimentos.filter((m) => m.situacao === "divergente");
  const conciliados = movimentos.filter((m) => m.situacao === "conciliado");

  const idsCasados = new Set(movimentos.map((m) => m.lancamentoId).filter(Boolean));
  const semMovimento = lancamentos.dados.filter(
    (l) => ["aprovado", "pago"].includes(l.status) && !idsCasados.has(l.id),
  );

  const candidatos = (m: MovimentoExtrato) =>
    lancamentos.dados.filter(
      (l) => CONCILIAVEIS.includes(l.status) && (!idsCasados.has(l.id) || l.id === m.lancamentoId),
    );

  function abrir(p: Pendencia) {
    setPendencia(p);
    setJustificativa("");
  }

  async function executar(fn: () => Promise<unknown>, id: string, sucesso: string) {
    setOcupado(id);
    try {
      await fn();
      toast.success(sucesso);
      setPendencia(null);
      setJustificativa("");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }

  async function adicionar() {
    await executar(
      () =>
        adicionarMovimento({
          contaBancariaId: novo.contaBancariaId,
          data: novo.data,
          descricao: novo.descricao,
          valorCentavos: paraCentavos(novo.valor),
        }),
      "novo",
      "Movimento adicionado — fica divergente até ser tratado.",
    );
    setNovo((s) => ({ ...s, descricao: "", valor: "" }));
  }

  /**
   * Casar não é só ligar dois registros: é afirmar que o dinheiro do extrato é
   * aquele lançamento. Quando os valores não batem, em vez de recusar em
   * silêncio, pedimos a justificativa e os dois ficam marcados como divergentes.
   */
  async function casar(m: MovimentoExtrato, lancamentoId: string) {
    const lanc = lancamentos.dados.find((l) => l.id === lancamentoId);
    if (!lanc) return;
    const diferenca = Math.abs(m.valorCentavos) - Math.abs(lanc.valorCentavos);

    if (diferenca !== 0) {
      abrir({ tipo: "divergencia", movimentoId: m.id, lancamentoId, diferenca });
      return;
    }
    await executar(
      () => conciliarMovimento({ movimentoId: m.id, lancamentoId }),
      m.id,
      "Movimento conciliado.",
    );
  }

  return (
    <>
      <PageHeader
        title="Conciliação bancária"
        subtitle="Movimento sem lançamento e lançamento pago sem movimento ficam divergentes até alguém tratar. Nada some sozinho."
      />

      <GradeFios colunas={3}>
        <Stat
          label="Movimentos divergentes"
          value={String(divergentes.length)}
          hint="Sem lançamento, ou com valor que não bate"
          tone={divergentes.length ? "warning" : "default"}
        />
        <Stat
          label="Lançamentos sem movimento"
          value={String(semMovimento.length)}
          tone={semMovimento.length ? "warning" : "default"}
        />
        <Stat label="Movimentos conciliados" value={String(conciliados.length)} />
      </GradeFios>

      {podeEscrever ? (
        <div className="mt-6">
          <ImportarExtrato />
        </div>
      ) : null}

      {podeEscrever ? (
        <Panel className="mt-4 grid gap-4 p-5 sm:grid-cols-5">
          <Campo label="Conta" className="sm:col-span-2">
            <Escolha
              value={novo.contaBancariaId}
              onChange={(v) => setNovo((s) => ({ ...s, contaBancariaId: v }))}
              placeholder="Selecionar conta"
              options={contas.dados.map((c) => ({ id: c.id, label: c.nome }))}
            />
          </Campo>
          <Campo label="Data">
            <Input
              type="date"
              value={novo.data}
              onChange={(e) => setNovo((s) => ({ ...s, data: e.target.value }))}
            />
          </Campo>
          <Campo label="Descrição">
            <Input
              value={novo.descricao}
              onChange={(e) => setNovo((s) => ({ ...s, descricao: e.target.value }))}
            />
          </Campo>
          <Campo label="Valor" hint="Negativo para saída.">
            <div className="flex gap-2">
              <Input
                className="num"
                inputMode="decimal"
                placeholder="0,00"
                value={novo.valor}
                onChange={(e) => setNovo((s) => ({ ...s, valor: e.target.value }))}
              />
              <Button onClick={() => void adicionar()} disabled={ocupado === "novo"}>
                Add
              </Button>
            </div>
          </Campo>
        </Panel>
      ) : null}

      <Panel className="mt-4 overflow-x-auto">
        <PanelHeader title="Extrato" />
        <table className="w-full min-w-[980px] text-sm">
          <thead>
            <tr>
              <Th>Data</Th>
              <Th>Conta</Th>
              <Th>Descrição</Th>
              <Th className="text-right">Valor</Th>
              <Th>Situação</Th>
              <Th>Tratamento</Th>
            </tr>
          </thead>
          <tbody>
            {movimentos.map((m) => (
              <tr key={m.id}>
                <Td className="num whitespace-nowrap">{data(m.data)}</Td>
                <Td className="text-muted-foreground">{nomeConta.get(m.contaBancariaId) ?? "—"}</Td>
                <Td>
                  <div className="max-w-[220px] truncate">{m.descricao}</div>
                  {m.justificativa ? (
                    <div className="label-caps truncate">{m.justificativa}</div>
                  ) : null}
                </Td>
                <Td
                  className={`num text-right ${m.valorCentavos < 0 ? "text-negative" : "text-positive"}`}
                >
                  {brl(m.valorCentavos)}
                </Td>
                <Td>
                  <StatusBadge status={m.situacao} />
                  {m.diferencaCentavos ? (
                    <div className="label-caps text-negative">
                      difere {brl(m.diferencaCentavos)}
                    </div>
                  ) : null}
                </Td>
                <Td>
                  {!podeEscrever ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : m.situacao === "divergente" && !m.lancamentoId ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Escolha
                        className="w-full sm:w-64"
                        value=""
                        onChange={(v) => void casar(m, v)}
                        placeholder="Casar com lançamento"
                        options={candidatos(m).map((l) => ({
                          id: l.id,
                          label: `${l.descricao} · ${brl(l.valorCentavos)}`,
                        }))}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={ocupado === m.id}
                        onClick={() => abrir({ tipo: "tratar", movimentoId: m.id })}
                      >
                        Tratar
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={ocupado === m.id}
                      onClick={() =>
                        void executar(
                          () => desfazerConciliacao({ movimentoId: m.id }),
                          m.id,
                          "Movimento voltou a divergente.",
                        )
                      }
                    >
                      Desfazer
                    </Button>
                  )}

                  {pendencia?.movimentoId === m.id ? (
                    <div className="mt-2 max-w-md">
                      <Textarea
                        rows={2}
                        autoFocus
                        value={justificativa}
                        onChange={(e) => setJustificativa(e.target.value)}
                        placeholder={
                          pendencia.tipo === "divergencia"
                            ? `Os valores diferem em ${brl(pendencia.diferenca)}. Por que casar assim mesmo?`
                            : "Por que este movimento nunca terá lançamento? (tarifa, rendimento…)"
                        }
                      />
                      <div className="mt-2 flex gap-2">
                        <Button
                          size="sm"
                          disabled={ocupado === m.id || justificativa.trim().length === 0}
                          onClick={() =>
                            void executar(
                              () =>
                                pendencia.tipo === "divergencia"
                                  ? conciliarMovimento({
                                      movimentoId: m.id,
                                      lancamentoId: pendencia.lancamentoId,
                                      justificativa: justificativa.trim(),
                                    })
                                  : tratarMovimento({
                                      movimentoId: m.id,
                                      justificativa: justificativa.trim(),
                                    }),
                              m.id,
                              pendencia.tipo === "divergencia"
                                ? "Divergência registrada — continua pendente até o valor ser acertado."
                                : "Movimento tratado.",
                            )
                          }
                        >
                          Confirmar
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setPendencia(null)}>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
        {movimentos.length === 0 ? (
          <EmptyState
            title="Extrato vazio"
            hint="Registre os movimentos da conta para conciliar."
          />
        ) : null}
      </Panel>

      <Panel className="mt-4 overflow-hidden">
        <PanelHeader title="Lançamentos pagos sem movimento no extrato" />
        {semMovimento.length === 0 ? (
          <EmptyState title="Nada pendente" />
        ) : (
          <ul className="divide-y divide-hairline">
            {semMovimento.map((l) => (
              <li key={l.id} className="flex justify-between gap-3 px-4 py-3 text-sm">
                <span className="min-w-0 truncate">{l.descricao}</span>
                <span className="num shrink-0">{brl(l.valorCentavos)}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}
